import { useEffect, useState } from "react";

interface Build {
  version: string;
  commit: string;
  builtAt: string | null;
}

/** This copy of the app, as it was built. */
const RUNNING: Build = { version: __APP_VERSION__, commit: __APP_COMMIT__, builtAt: __APP_BUILT_AT__ };

const CHECK_EVERY_MS = 10 * 60 * 1000;

/**
 * The version in the menu, and whether the server has a newer one. A phone
 * can hold on to an old bundle through the offline cache; this says so and
 * offers a reload that fetches the new one.
 */
export function VersionTag() {
  const [server, setServer] = useState<Build | null>(null);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (import.meta.env.VITE_DEMO === "true") return;
    let alive = true;
    const check = () =>
      fetch("/api/version", { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((b: Build | null) => alive && b && setServer(b))
        .catch(() => undefined);
    void check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onFocus = () => document.visibilityState === "visible" && void check();
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const behind = server != null && server.commit !== "unknown" && server.commit !== RUNNING.commit;

  async function reload() {
    setReloading(true);
    // Drop the offline copy so the reload fetches the new bundle.
    try {
      const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.all(registrations.map((r) => r.unregister()));
      if ("caches" in window) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    } finally {
      window.location.reload();
    }
  }

  const built = RUNNING.builtAt ? new Date(RUNNING.builtAt) : null;
  return (
    <div className="mt-2 text-[11px] leading-snug text-ink-400">
      <span title={built ? `Built ${built.toLocaleString("en-IN")}` : undefined}>
        Version {RUNNING.version} · {RUNNING.commit}
      </span>
      {server && !behind && <span className="text-ink-400"> · up to date</span>}
      {behind && (
        <div className="mt-1 rounded-md bg-amber-50 px-2 py-1.5 text-amber-800">
          Version {server.version} is live.{" "}
          <button onClick={reload} disabled={reloading} className="font-medium underline">
            {reloading ? "Updating…" : "Update now"}
          </button>
        </div>
      )}
    </div>
  );
}
